import React, { useState } from 'react';
import { type CSSProperties } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';

export interface SaveExitBarProps {
  onSaveExit: () => void;
}

const PULSE_KEYFRAMES = `
@keyframes save-exit-bar-pulse {
  0%   { box-shadow: 0 0 0 0   rgba(21, 21, 22, 0.6); }
  70%  { box-shadow: 0 0 0 10px rgba(21, 21, 22, 0); }
  100% { box-shadow: 0 0 0 0   rgba(21, 21, 22, 0); }
}
`;

const BUTTON_WRAP_BREAKPOINT = 504;

export function SaveExitBar({ onSaveExit }: SaveExitBarProps): React.ReactElement {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const buttonBelowText = useIsMobileViewport(BUTTON_WRAP_BREAKPOINT);
  const [isBarHovered, setIsBarHovered] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  const bar: CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxSizing: 'border-box',
    width: '100%',
    minHeight: '62px',
    backgroundColor: isBarHovered ? '#FFFFFF' : tokens.backgrounds.surface,
    boxShadow: isBarHovered ? '0 3px 14px rgba(0, 0, 0, 0.12)' : 'none',
    transition: 'background-color 130ms ease, box-shadow 180ms ease',
    borderTop: 'none',
    borderRight: 'none',
    borderBottom: `1px solid ${tokens.borders.default}`,
    borderLeft: 'none',
    padding: '12px 13px 12px 23px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.md,
    fontFamily: tokens.typography.fontFamily,
  };

  const copy: CSSProperties = {
    flex: buttonBelowText ? '1 1 100%' : '1 1 0',
    color: tokens.text.primary,
    fontSize: isMobile ? tokens.typography.sizes.base : tokens.typography.sizes.ui,
    fontWeight: tokens.typography.weights.regular,
    lineHeight: tokens.typography.lineHeights.normal,
    margin: 0,
    minWidth: 0,
  };

  const bold: CSSProperties = {
    fontWeight: tokens.typography.weights.bold,
  };

  const button: CSSProperties = {
    boxSizing: 'border-box',
    flexShrink: 0,
    marginLeft: buttonBelowText ? 0 : 'auto',
    height: '38px',
    padding: '10px 30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.md,
    backgroundColor: isButtonHovered ? '#252526' : '#151516',
    color: '#FFFFFF',
    fontFamily: tokens.typography.fontFamily,
    fontSize: tokens.typography.sizes.base,
    fontWeight: tokens.typography.weights.bold,
    lineHeight: tokens.typography.lineHeights.normal,
    border: 0,
    borderRadius: 0,
    cursor: 'pointer',
    animation: isBarHovered
      ? 'save-exit-bar-pulse 1.3s ease-out infinite'
      : undefined,
  };

  return (
    <div
      style={bar}
      onMouseEnter={() => setIsBarHovered(true)}
      onMouseLeave={() => setIsBarHovered(false)}
    >
      <style>{PULSE_KEYFRAMES}</style>
      <p style={copy}>
        {buttonBelowText ? t('save_exit_bar.warning') : t('save_exit_bar.warning_desktop')}
      </p>
      <button
        type="button"
        style={button}
        onClick={onSaveExit}
        onMouseEnter={() => setIsButtonHovered(true)}
        onMouseLeave={() => setIsButtonHovered(false)}
        aria-label={t('save_exit_bar.aria_save_exit')}
      >
        <span>{t('save_exit_bar.save_exit')}</span>
        <SaveExitIcon />
      </button>
    </div>
  );
}

SaveExitBar.displayName = 'SaveExitBar';

function SaveExitIcon(): React.ReactElement {
  return (
    <svg
      width="19"
      height="17"
      viewBox="0 0 19 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M5.93779 2.42857C6.59466 2.42857 7.12535 1.88594 7.12535 1.21429C7.12535 0.542634 6.59466 0 5.93779 0H3.56267C1.59578 0 0 1.6317 0 3.64286V13.3571C0 15.3683 1.59578 17 3.56267 17H5.93779C6.59466 17 7.12535 16.4574 7.12535 15.7857C7.12535 15.1141 6.59466 14.5714 5.93779 14.5714H3.56267C2.90581 14.5714 2.37512 14.0288 2.37512 13.3571V3.64286C2.37512 2.97121 2.90581 2.42857 3.56267 2.42857H5.93779ZM18.6521 9.35759C19.116 8.88326 19.116 8.11295 18.6521 7.63862L13.9019 2.78147C13.438 2.30714 12.6846 2.30714 12.2207 2.78147C11.7568 3.2558 11.7568 4.02612 12.2207 4.50045L14.9447 7.28571H7.12535C6.46848 7.28571 5.93779 7.82835 5.93779 8.5C5.93779 9.17165 6.46848 9.71429 7.12535 9.71429H14.9447L12.2207 12.4996C11.7568 12.9739 11.7568 13.7442 12.2207 14.2185C12.6846 14.6929 13.438 14.6929 13.9019 14.2185L18.6521 9.36138V9.35759Z"
        fill="#FFFFFF"
      />
    </svg>
  );
}
